// src/pages/AccountTeam.js
// Account-scoped persistent team roster. Lets a caterer manage their
// "regulars" — staff that get bulk-attached to every new event with one
// click, instead of being re-invited by name each time.

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { confirm, toast } from '../components/Shared/feedback';

const ROLE_OPTIONS = [
	{ value: 'member', label: 'Member (read-only)' },
	{ value: 'server', label: 'Server (floor staff)' },
	{ value: 'kitchen', label: 'Kitchen staff' },
	{ value: 'admin', label: 'Admin (full access)' },
];

const ROLE_LABEL = ROLE_OPTIONS.reduce((acc, r) => ({ ...acc, [r.value]: r.label }), {});

const AccountTeam = () => {
	const [team, setTeam] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [adding, setAdding] = useState(false);
	const [addEmail, setAddEmail] = useState('');
	const [addRole, setAddRole] = useState('member');
	const [addNotes, setAddNotes] = useState('');

	const load = async () => {
		setLoading(true);
		try {
			const res = await api.get('/team');
			setTeam(res.data?.team || []);
		} catch (err) {
			setError(err.response?.data?.msg || 'Failed to load team.');
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => { load(); }, []);

	const handleAdd = async (e) => {
		e.preventDefault();
		if (!addEmail.trim()) {
			toast.error('Enter an email.');
			return;
		}
		setAdding(true);
		try {
			await api.post('/team', { email: addEmail.trim(), defaultRole: addRole, notes: addNotes.trim() });
			toast.success('Added to roster.');
			setAddEmail('');
			setAddNotes('');
			setAddRole('member');
			await load();
		} catch (err) {
			toast.error(err.response?.data?.msg || 'Failed to add.');
		} finally {
			setAdding(false);
		}
	};

	const handleRoleChange = async (id, newRole) => {
		try {
			await api.put(`/team/${id}`, { defaultRole: newRole });
			setTeam((prev) => prev.map((m) => (m._id === id ? { ...m, defaultRole: newRole } : m)));
		} catch (err) {
			toast.error('Failed to update role.');
		}
	};

	const handleRemove = async (member) => {
		const name = member.user?.name || member.user?.email || 'this person';
		const ok = await confirm({
			title: `Remove ${name} from your roster?`,
			message: 'They\'ll stay on any events they\'re already part of. They just won\'t be bulk-attached to new ones.',
			tone: 'danger',
			confirmLabel: 'Remove',
		});
		if (!ok) return;
		try {
			await api.delete(`/team/${member._id}`);
			toast.success('Removed from roster.');
			setTeam((prev) => prev.filter((m) => m._id !== member._id));
		} catch (err) {
			toast.error('Failed to remove.');
		}
	};

	return (
		<div className="ev-user-shell">
			<header className="ev-admin-topbar">
				<div className="ev-admin-topbar-inner">
					<div className="ev-admin-topbar-left">
						<div className="ev-brand-lockup">
							<img src="/images/eventory-logo.svg" alt="Eventory" />
						</div>
					</div>
					<div className="ev-admin-topbar-actions">
						<Link to="/dashboard" className="ev-soft-btn">My events</Link>
						<Link to="/account" className="ev-soft-btn">Account</Link>
					</div>
				</div>
			</header>

			<main className="ev-user-dash">
				<div className="ev-user-wrap">
					<div className="ev-admin-header">
						<div>
							<h2 className="ev-section-title">My team</h2>
							<div className="ev-subtle">Regulars you can bulk-add to every new event.</div>
						</div>
					</div>

					{error && <div className="ev-alert ev-alert-danger">{error}</div>}

					<section className="ev-admin-section ev-flat-card">
						<h3 className="ev-zero-margin">Add to roster</h3>
						<p className="ev-subtle ev-top-gap" style={{ marginBottom: 16 }}>
							The person must already have an Eventory account. If they don\'t, invite them to one of your events first — they\'ll show up here automatically once they sign up, and you can promote them to your roster.
						</p>
						<form onSubmit={handleAdd} className="ev-stack" style={{ gap: 12 }}>
							<div className="ev-field-block">
								<label htmlFor="team-email">Email</label>
								<input
									id="team-email"
									type="email"
									className="ev-input"
									value={addEmail}
									onChange={(e) => setAddEmail(e.target.value)}
									placeholder="staff@example.com"
									required
								/>
							</div>
							<div className="ev-field-block">
								<label htmlFor="team-role">Default role</label>
								<select
									id="team-role"
									className="ev-input"
									value={addRole}
									onChange={(e) => setAddRole(e.target.value)}
								>
									{ROLE_OPTIONS.map((r) => (
										<option key={r.value} value={r.value}>{r.label}</option>
									))}
								</select>
							</div>
							<div className="ev-field-block">
								<label htmlFor="team-notes">Notes (optional)</label>
								<input
									id="team-notes"
									type="text"
									className="ev-input"
									value={addNotes}
									onChange={(e) => setAddNotes(e.target.value)}
									placeholder="e.g. Always assigns to grill station"
									maxLength={500}
								/>
							</div>
							<div>
								<button type="submit" className="ev-primary-btn" disabled={adding}>
									{adding ? 'Adding…' : 'Add to roster'}
								</button>
							</div>
						</form>
					</section>

					<section className="ev-admin-section ev-flat-card">
						<h3 className="ev-zero-margin">Roster ({team.length})</h3>
						{loading ? (
							<div className="ev-subtle ev-top-gap">Loading…</div>
						) : team.length === 0 ? (
							<div className="ev-empty ev-top-gap">
								<strong>No regulars yet.</strong>
								<div className="ev-subtle">Add staff above. Once your roster has people, you\'ll see a "Add my regular team" option in the create-event flow.</div>
							</div>
						) : (
							<table className="ev-data-table ev-top-gap">
								<thead>
									<tr>
										<th>Name</th>
										<th>Email</th>
										<th>Default role</th>
										<th>Notes</th>
										<th>Active in</th>
										<th></th>
									</tr>
								</thead>
								<tbody>
									{team.map((m) => (
										<tr key={m._id}>
											<td>{m.user?.name || '—'}</td>
											<td className="ev-subtle">{m.user?.email}</td>
											<td>
												<select
													className="ev-input"
													value={m.defaultRole}
													onChange={(e) => handleRoleChange(m._id, e.target.value)}
													style={{ minWidth: 160 }}
												>
													{ROLE_OPTIONS.map((r) => (
														<option key={r.value} value={r.value}>{ROLE_LABEL[r.value]}</option>
													))}
												</select>
											</td>
											<td className="ev-subtle">{m.notes || '—'}</td>
											<td>{m.activeEventCount} event{m.activeEventCount === 1 ? '' : 's'}</td>
											<td>
												<button
													type="button"
													className="ev-soft-btn"
													onClick={() => handleRemove(m)}
												>
													Remove
												</button>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						)}
					</section>
				</div>
			</main>
		</div>
	);
};

export default AccountTeam;
